import { AnyAction } from 'redux';
import {
  DataForDashboard,
  GlobalActions,
  PreiseForDashboard,
  ProduktDeleted,
  TaasNachlassVorteilReceivedPayload,
  ZahlungsoptionenChanged,
} from '@ps20-frontend/shop-integration-redux';
import { App, getAppTitle, getSpartenTypeByApp } from '@ps20-frontend/frontend-models';
import {
  NachlaesseForAngebot,
  NachlassVorteilFromPcnm,
} from '@ps20/bff-models/dist/lib/ps20-bff-model-taas';
import { IAppState } from '@ps20-frontend/apps/toolbar-store';
import { convertZahlweiseInMonthInYear } from '@ps20-frontend/preis-lib';
import { AgencyFinanceAccount } from '@ps20/bff-models';
import {
  ChangeVorteilsrabattForProduktPayload,
  Vorteilsrabatt,
  VorteilsrabattProdukt,
} from '../interfaces/vorteilsrabatt';
import { ToolbarActions } from '../toolbar.actions';

export const vorteilsrabattReducer = function (
  sectionState: Vorteilsrabatt = null,
  action: AnyAction,
  state: IAppState,
): Vorteilsrabatt {
  switch (action.type) {
    case GlobalActions.IN_DASHBOARD_UEBERNEHMEN: {
      const { appId, angebotsId, versichertesRisiko } = action.payload as DataForDashboard;
      if (appId === App.JHP || appId === App.GEGE) {
        return sectionState;
      }

      const existingProdukt = sectionState.produkte.find(
        produkt => produkt.appId === appId && produkt.angebotsId === angebotsId,
      );

      if (existingProdukt) {
        return {
          ...sectionState,
          produkte: sectionState.produkte?.map(produkt =>
            produkt === existingProdukt
              ? { ...produkt, versichertesRisiko, angebotsId: angebotsId }
              : produkt,
          ),
        };
      } else {
        const newProdukt: VorteilsrabattProdukt = {
          appId,
          angebotsId,
          title: getAppTitle(appId),
          versichertesRisiko,
          kundenbeitragForZahlweise: 0,
          bruttopreis: 0,
          kundenbeitragJaehrlich: 0,
          rabattInProzent: 0,
          differenzVertreterkonto: 0,
          erhoehteBelastungAb: 0,
          keineBelastungBis: 0,
          max: 0,
          startgutschriftPrz: 0,
          kipp: 0,
          jahresNettoBausteineSum: 0,
          cpSternBausteine: 0,
          belastfakNiedrig: 0,
          belastfakHoch: 0,
        };

        return {
          ...sectionState,
          produkte: [...sectionState.produkte, newProdukt],
        };
      }
    }
    case GlobalActions.PRODUKT_DELETED: {
      const { angebotsIds } = action.payload as ProduktDeleted;
      const produkte = sectionState.produkte.filter(
        produkt =>
          !angebotsIds.some(
            ({ appId, angebotsId }) => produkt.appId === appId && produkt.angebotsId === angebotsId,
          ),
      );
      return {
        ...sectionState,
        produkte: produkte,
      };
    }
    case GlobalActions.PREISE_INS_DASHBOARD_UEBERNEHMEN: {
      const { appId, angebotsId, bruttopreis }: PreiseForDashboard = action.payload;
      const produkte = sectionState.produkte?.map(produkt =>
        produkt?.appId === appId && produkt?.angebotsId === angebotsId
          ? {
              ...produkt,
              bruttopreis,
            }
          : produkt,
      );
      return {
        ...sectionState,
        produkte,
      };
    }
    case ToolbarActions.CHANGE_VORTEILSRABATT_FOR_PRODUKT: {
      const { appId, angebotsId, value } = action.payload as ChangeVorteilsrabattForProduktPayload;
      const produkte = sectionState.produkte?.map(produkt =>
        produkt.appId === appId && produkt.angebotsId === angebotsId
          ? {
              ...produkt,
              ...findBeitraege(produkt, state, value),
              rabattInProzent: value,
              max: produkt.max,
            }
          : produkt,
      );

      return {
        ...sectionState,
        ...gesamtZahlenForRabattProdukte(produkte),
        produkte,
      };
    }
    case ToolbarActions.SWITCH_SHOW_KONTOBEWEGUNGEN: {
      return { ...sectionState, showKontobewegungen: action.payload };
    }
    case ToolbarActions.KONTAFLEX_RECEIVED: {
      const kontaflex: AgencyFinanceAccount = action.payload;
      return {
        ...sectionState,
        kontostand: kontaflex.accountBalance,
        kredit: kontaflex.creditLimit,
      };
    }
    case ToolbarActions.RABATT_SERVICE_NACHLAESSE_SUCCESS: {
      const rabattServiceResponse = action.payload as Array<NachlaesseForAngebot>;
      if (!rabattServiceResponse) {
        return sectionState;
      }
      const produkte = sectionState.produkte.map(produkt => {
        const vorteilsRabattForProdukt = findVorteilsrabattProdukt(produkt, rabattServiceResponse);
        const nachlaesse = vorteilsRabattForProdukt.nachlaesse;
        const vorteilsRabattFromService = nachlaesse?.find(
          nachlassVorteilFromPcnm => nachlassVorteilFromPcnm.rabattId === produkt.rabattInProzent,
        );

        produkt.kundenbeitragForZahlweise =
          vorteilsRabattFromService?.beitragFuerKunden /
          convertZahlweiseInMonthInYear(state.zahlungsoptionen.zahlungsweise);
        produkt.kundenbeitragJaehrlich = vorteilsRabattFromService?.beitragFuerKunden;
        produkt.differenzVertreterkonto =
          vorteilsRabattFromService?.belaustungOrGutschriftVertreter;
        produkt.keineBelastungBis = findMinimumBelastungRabattId(nachlaesse);
        produkt.erhoehteBelastungAb = vorteilsRabattFromService?.kipp;
        return produkt;
      });

      return {
        ...sectionState,
        ...gesamtZahlenForRabattProdukte(produkte),
        produkte,
      };
    }
    case GlobalActions.TAAS_NACHLASS_RABATT_RECEIVED: {
      const payload = action.payload as TaasNachlassVorteilReceivedPayload;
      return {
        ...sectionState,
        produkte: sectionState.produkte.map(produkt =>
          produkt.appId === payload.appId && produkt.angebotsId === payload.angebotsId
            ? {
                ...produkt,
                rabattInProzent: payload.taasNachlassVorteil.nachlass,
                max: payload.taasNachlassVorteil.max,
                belastfakNiedrig: payload.taasNachlassVorteil.belastfakNiedrig,
                belastfakHoch: payload.taasNachlassVorteil.belastfakHoch,
                kipp: payload.taasNachlassVorteil.kipp,
                startgutschriftPrz: payload.taasNachlassVorteil.startgutschriftPrz,
                jahresNettoBausteineSum: payload.salesProduktVariants[0].jahresNettoBausteineSum,
                cpSternBausteine: payload.salesProduktVariants[0].cpSternSum,
              }
            : produkt,
        ),
      };
    }
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED:
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED_VORVERTRAG: {
      const zahlungsoptionen: ZahlungsoptionenChanged = action.payload;
      if (zahlungsoptionen) {
        sectionState.produkte.forEach(produkt => {
          produkt.kundenbeitragForZahlweise =
            produkt.kundenbeitragJaehrlich /
            convertZahlweiseInMonthInYear(state.zahlungsoptionen.zahlungsweise);
        });
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};

export function findVorteilsrabattProdukt(
  vorteilsRabattProdukt: VorteilsrabattProdukt,
  nachlaesseForAngebot: Array<NachlaesseForAngebot>,
): NachlaesseForAngebot {
  return nachlaesseForAngebot?.find(
    nachlassForAngebot =>
      nachlassForAngebot.spartenType === getSpartenTypeByApp(vorteilsRabattProdukt.appId) &&
      nachlassForAngebot.angebotsId?.trim() === vorteilsRabattProdukt.angebotsId?.trim(),
  );
}

export function findBeitraege(
  vorteilsRabattProdukt: VorteilsrabattProdukt,
  state: IAppState,
  valuePercent: number,
): {
  differenzVertreterkonto: number;
  kundenbeitragForZahlweise: number;
  kundenbeitragJaehrlich: number;
} {
  const nachlaesseForAngebot: Array<NachlaesseForAngebot> = state.receivedNachlaesseForAlleAngebote;
  const zahlweise = state.zahlungsoptionen.zahlungsweise;

  const vorteilRabattProdukt = findVorteilsrabattProdukt(
    vorteilsRabattProdukt,
    nachlaesseForAngebot,
  );
  const foundedVorteilRabattForPercent = vorteilRabattProdukt?.nachlaesse?.find(
    nachlass => nachlass.rabattId === valuePercent,
  );
  return {
    differenzVertreterkonto: foundedVorteilRabattForPercent?.belaustungOrGutschriftVertreter,
    kundenbeitragForZahlweise:
      foundedVorteilRabattForPercent?.beitragFuerKunden / convertZahlweiseInMonthInYear(zahlweise),
    kundenbeitragJaehrlich: foundedVorteilRabattForPercent?.beitragFuerKunden,
  };
}

export function gesamtZahlenForRabattProdukte(produkte: Array<VorteilsrabattProdukt>): {
  gesamtbeitragFuerKunden: number;
  gesamtDifferenzVertreterkonto: number;
} {
  const gesamtbeitragFuerKunden = produkte.reduce(
    (acc, produkt) => acc + produkt.kundenbeitragForZahlweise,
    0,
  );
  const gesamtDifferenzVertreterkonto = produkte.reduce(
    (acc, produkt) => acc + produkt.differenzVertreterkonto,
    0,
  );
  return {
    gesamtbeitragFuerKunden,
    gesamtDifferenzVertreterkonto,
  };
}

export function findMinimumBelastungRabattId(nachlaesse: Array<NachlassVorteilFromPcnm>): number {
  const nachlaesseWithPositiveBelastung = nachlaesse.filter(
    nachlassVorteil => nachlassVorteil?.belaustungOrGutschriftVertreter >= 0,
  );

  if (nachlaesseWithPositiveBelastung.length === 0) {
    return 0;
  }

  return nachlaesseWithPositiveBelastung.reduce(
    (min, current) =>
      current.belaustungOrGutschriftVertreter < min.belaustungOrGutschriftVertreter ? current : min,
    nachlaesseWithPositiveBelastung[0],
  ).rabattId;
}
